import React from 'react';

interface SSOErrorPageProps {
  /**
   * Error message to display
   */
  error: string;
  /**
   * Support email address
   */
  supportEmail: string;
  /**
   * Function to call when user clicks "Try again"
   */
  onRetry: () => void;
  /**
   * Brand name for the error message
   */
  brandName?: string;
  /**
   * Translation function for account namespace
   */
  t: (key: string, options?: any) => string;
}

/**
 * Shared SSO Error Page component
 * Shows user-friendly error messages for SSO authentication failures
 */
export const SSOErrorPage: React.FC<SSOErrorPageProps> = ({
  error,
  supportEmail,
  onRetry,
  brandName = 'Application',
  t
}) => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md rounded-lg bg-white px-8 py-12 shadow-lg">
        <div className="text-center">
          <h1 className="mb-6 text-3xl font-bold text-red-600">{t('sso.error.accountNotFound')}</h1>
          <p className="mb-8 leading-relaxed text-gray-700">
            {error}
          </p>
        </div>
        <div className="flex items-center justify-center gap-6">
          <a
            href={`mailto:${supportEmail}`}
            className="rounded-lg bg-red-600 px-6 py-3 font-medium text-white shadow-sm transition-colors duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-2"
            data-testid="sso-error-contact-support-link"
          >
            {t('sso.error.contactSupport')}
          </a>
          <span className="font-medium text-gray-500">{t('sso.error.or')}</span>
          <button
            onClick={onRetry}
            className="rounded-lg px-6 py-3 font-medium text-red-600 underline underline-offset-4 transition-colors duration-200 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-2"
            data-testid="sso-error-retry-button"
          >
            {t('sso.error.tryAgain')}
          </button>
        </div>
      </div>
    </div>
  );
};